import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import {
  products,
  stats,
  testimonials,
  whyChooseUs,
  banners,
  certificateHome,
} from "../mockData";
import {
  ArrowRight,
  Award,
  Globe,
  Users,
  Leaf,
  Settings,
  Star,
  CheckCircle2,
  X,
} from "lucide-react";
import QuickInquiry from "../components/QuickInquiry";
import Seo from "../components/Seo";
import "swiper/css";
import "swiper/css/pagination";

const iconMap = {
  award: Award,
  globe: Globe,
  users: Users,
  // truck: Truck,
  leaf: Leaf,
  settings: Settings,
};

const Home = () => {
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <div className="min-h-screen">
      <Seo
        title="Textile Dyes Manufacturer in India | Exporter to Bangladesh & Vietnam"
        description="Meet Enterprise is a trusted textile dyes manufacturer and exporter in India, supplying high-quality dye chemicals to Bangladesh, Vietnam, and global textile industries with consistent performance and reliability."
        keywords="textile dyes manufacturer India, textile dyes exporter Bangladesh, fabric dyeing chemicals supplier, dye chemicals Gujarat"
        canonical="https://meet-enterprise.com/"
      />
      {/* Hero Banner */}
      <section className="relative w-full overflow-hidden bg-[#0b0b0b]">
        <Swiper
          modules={[Autoplay, Pagination]}
          slidesPerView={1}
          loop={true}
          speed={900}
          autoplay={{
            delay: 4500,
            disableOnInteraction: false,
          }}
          pagination={{
            clickable: true,
          }}
          className="heroBannerSwiper"
        >
          {banners.map((banner, index) => (
            <SwiperSlide key={index}>
              <div className="relative w-full h-[600px] overflow-hidden bg-[#0b0b0b]">
                {/* Desktop */}
                <img
                  src={banner.desktop}
                  alt={`Banner ${index + 1}`}
                  loading="lazy"
                  className="hidden lg:block w-full h-full object-fill object-center"
                />

                {/* Tablet */}
                <img
                  src={banner.tablet}
                  alt={`Banner ${index + 1}`}
                  loading="lazy"
                  className="hidden md:block lg:hidden w-full h-full object-fill object-center"
                />

                {/* Mobile */}
                <img
                  src={banner.mobile}
                  alt={`Banner ${index + 1}`}
                  loading="lazy"
                  className="block md:hidden w-full h-full object-fill object-center"
                />

                {/* Premium Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-black/10"></div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 bg-white border-b -mt-1">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl hover:shadow-lg transition-shadow"
              >
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Products Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <div className="inline-flex px-4 py-2 rounded-full bg-purple-100 text-purple-700 text-sm font-semibold mb-4">
              Our Products
            </div>

            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Explore Our Products
            </h2>

            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Premium dyes engineered for vibrant shades, performance and
              industrial consistency.
            </p>
          </motion.div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 items-stretch">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="group bg-white rounded-3xl border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col h-full"
              >
                {/* Banner */}
                <div className="relative h-60 overflow-hidden">
                  <img
                    src={
                      Array.isArray(product.image)
                        ? product.image[0]
                        : product.image
                    }
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>

                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-white text-3xl font-bold">
                      {product.title}
                    </h3>

                    {product.subtitle && (
                      <p className="text-white/90 text-xs uppercase tracking-wide mt-1">
                        {product.subtitle}
                      </p>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-grow">
                  {/* Color Palette */}
                  {product.colorPalette && (
                    <div className="mb-5">
                      <p className="text-sm font-semibold text-gray-900 mb-3">
                        Shade Range
                      </p>

                      <div className="flex gap-2">
                        {product.colorPalette?.slice(0, 5).map((item, i) => (
                          <div key={i} className="text-center flex-1">
                            <div
                              className="w-8 h-8 rounded-lg border-2 border-white shadow mx-auto"
                              style={{ backgroundColor: item.color }}
                              title={item.name}
                            ></div>
                            <span className="text-[10px] text-gray-600 mt-1 block break-words leading-tight">
                              {item.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sub Types */}
                  {product.subTypes && (
                    <div className="mb-5">
                      <p className="text-sm font-semibold text-gray-900 mb-3">
                        Available Series
                      </p>

                      <div className="flex flex-wrap gap-2">
                        {product.subTypes.map((type, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200 hover:border-purple-300 hover:text-purple-700 transition"
                          >
                            {type.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-sm text-gray-600 leading-7 line-clamp-5 flex-grow">
                    {product.description}
                  </p>

                  {/* CTA */}
                  <Link
                    to={product.path}
                    className="mt-6 inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-semibold"
                  >
                    View Details
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* About Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
                About Us
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                <strong>
                  {" "}
                  Welcome to Meet Enterprise – Leading Mfg. & Exporter in color
                  dyes and chemicals.
                </strong>
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                At Meet Enterprise, we are a leading textile dyes manufacturer
                and exporter in India, offering high-performance colour dyes and
                fabric dyeing chemicals for textiles, leather, paint, and
                cosmetics industries. Our products are designed to deliver
                consistent color quality, excellent performance, and reliable
                results for your business success.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Over 15 years of industry experience, we supply high-quality
                textile dyeing chemicals that meet international standards,
                serving clients across India, Bangladesh, and Vietnam.
              </p>

              <Link
                to="/about"
                className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium rounded-lg transition-all transform hover:scale-105 shadow-lg"
              >
                <span>Explore More</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl blur-2xl opacity-20"></div>
              <img
                src="https://images.unsplash.com/photo-1587915521697-43fc4a4adf59?auto=format&fit=crop&w=800&h=600"
                alt="Fabric dyeing process"
                className="relative w-full h-auto rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
              Why Choose Us
            </div>

            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Core Competencies
            </h2>

            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
              Trusted manufacturing expertise, quality assurance, and scalable
              delivery.
            </p>
          </motion.div>

          {/* Cards Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8"
          >
            {[
              {
                number: "01",
                title: "Precision Engineering",
                desc: "Precision-engineered dyes for textiles, leather, cosmetics, and paints.",
              },
              {
                number: "02",
                title: "Custom Formulation",
                desc: "Custom formulation with consistent batch-to-batch quality.",
              },
              {
                number: "03",
                title: "Sustainable Production",
                desc: "Environmentally conscious manufacturing processes with reliable compliance.",
              },
              {
                number: "04",
                title: "Fast Global Delivery",
                desc: "Fast turnaround supported by efficient global logistics capabilities.",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -6 }}
                className="group relative bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-2xl transition-all duration-300 overflow-hidden"
              >
                {/* Top Gradient Line */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-600 to-pink-500"></div>

                {/* Number */}
                <div className="text-5xl md:text-6xl font-bold text-gray-100 absolute top-4 right-5 select-none">
                  {item.number}
                </div>

                {/* Content */}
                <div className="relative z-10">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 group-hover:text-purple-700 transition-colors">
                    {item.title}
                  </h3>

                  <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
              Testimonials
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>

            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Trusted by leading businesses across industries
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => {
              const ratingPercent = (testimonial.rating / 5) * 100;

              return (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow"
                >
                  {/* Star Fill Rating */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                      {/* <span className="text-sm font-semibold text-gray-700">
                        Rating
                      </span> */}

                      {/* <span className="text-sm font-bold text-purple-700">
                        {testimonial.rating}/5
                      </span> */}
                    </div>

                    <div className="relative inline-block">
                      {/* Gray Base Stars */}
                      <div className="flex gap-1 text-gray-300">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-6 h-6 fill-current" />
                        ))}
                      </div>

                      {/* Filled Stars Overlay */}
                      <div
                        className="absolute top-0 left-0 overflow-hidden"
                        style={{ width: `${ratingPercent}%` }}
                      >
                        <div className="flex gap-1 text-yellow-500 whitespace-nowrap">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="w-6 h-6 fill-current" />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-6 italic leading-relaxed">
                    "{testimonial.content}"
                  </p>

                  <div className="border-t border-gray-200 pt-4">
                    <p className="font-bold text-gray-900">
                      {testimonial.name}
                    </p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                    <p className="text-sm text-purple-600 font-medium">
                      {/* {testimonial.company} */}
                    </p>
                    <p className="text-sm text-gray-500">
                      {testimonial.location}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certificates Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
              Certifications
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Certifications
            </h2>
          </motion.div>

          <Swiper
            modules={[Autoplay, Pagination]}
            spaceBetween={24}
            loop={certificateHome.length > 3}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
            pagination={{ clickable: true }}
            className="heroBannerSwiper"
            breakpoints={{
              0: { slidesPerView: 1 },
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
          >
            {certificateHome.map((certificate, index) => (
              <SwiperSlide key={index}>
                <button
                  type="button"
                  onClick={() => setSelectedCertificate(certificate)}
                  className="w-full h-full rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 p-3"
                >
                  <img
                    src={certificate}
                    alt={`Certificate ${index + 1}`}
                    className="w-full h-[300px] sm:h-[340px] md:h-[360px] object-contain bg-gray-50 rounded-xl"
                  />
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-purple-600 to-pink-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white text-opacity-90 mb-8">
              Contact us today for a consultation or request a sample of our
              products
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {/* <button
                onClick={() => setInquiryOpen(true)}
                className="px-8 py-4 bg-white text-purple-700 font-semibold rounded-lg hover:bg-opacity-90 transition-all transform hover:scale-105 shadow-xl"
              >
                Request a Quote
              </button> */}
              <Link
                to="/contact"
                className="px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-purple-700 transition-all transform hover:scale-105"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <QuickInquiry
        isOpen={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
      />

      {selectedCertificate && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setSelectedCertificate(null)}
        >
          <div
            className="relative max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setSelectedCertificate(null)}
              className="absolute -top-12 right-0 text-white bg-white/20 hover:bg-white/30 rounded-full p-2 transition"
              aria-label="Close certificate preview"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={selectedCertificate}
              alt="Certificate Preview"
              className="w-full max-h-[85vh] object-contain rounded-xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
